'use client'

import { useEffect, useMemo, useState, type PropsWithChildren } from 'react'
import { Skeleton } from '@cocoonspace/ui/components/skeleton'
import { useIsMobile } from '@cocoonspace/ui/hooks/use-mobile'
import { cn } from '@cocoonspace/ui/lib/utils'
import { useScroll } from 'motion/react'
import { usePathname } from 'next/navigation'

import { SeminarHeader } from '~/domains/seminar/components/seminar-header'
import { stripLocaleFromPath } from '~/i18n/routing'
import { useAuth } from '~/providers/auth-provider'
import { AuthenticatedMenu } from './authenticated-menu'
import { BrandLink } from './brand-link'
import { FindACocoonBtn } from './find-a-cocoon-btn'
import { GuestMenu } from './guest-menu'
import { GuestMobileMenu } from './guest-mobile-menu'

const NavbarSkeleton = () => (
	<div className='flex flex-row items-center gap-8'>
		<Skeleton className='h-[16px] w-[106px] max-md:hidden' />
		<Skeleton className='h-[20px] w-[20px] max-md:hidden' />
		<Skeleton className='h-[40px] w-[80px] rounded-full max-md:hidden' />
	</div>
)

const useHomeScrollDetection = () => {
	const pathname = stripLocaleFromPath(usePathname() ?? '')
	const { scrollY } = useScroll()

	const [showTransparentBar, setShowTransparentBar] = useState(true)

	useEffect(() => {
		setShowTransparentBar(scrollY.get() < 450)

		const unsub = scrollY.on('change', (y) => {
			return setShowTransparentBar(y < 450)
		})

		return () => unsub()
	}, [scrollY])

	return useMemo(() => {
		if (pathname !== '/') return false
		return showTransparentBar
	}, [pathname, showTransparentBar])
}

const AppMenu = () => {
	const isMobile = useIsMobile()
	const { isAuth, isLoading } = useAuth()

	if (isLoading) return <NavbarSkeleton />
	if (!isAuth) return isMobile ? <GuestMobileMenu /> : <GuestMenu />
	return <AuthenticatedMenu />
}

export const AppNavbar = ({
	className,
}: PropsWithChildren<{ className?: string }>) => {
	const pathname = stripLocaleFromPath(usePathname() ?? '')
	const { isAuth } = useAuth()
	const showTransparentBar = useHomeScrollDetection()

	if (pathname === '/seminar') return <SeminarHeader />

	return (
		<nav
			className={cn(
				'border-border flex h-[var(--app-navbar-height)] w-full shrink-0 items-center justify-between border-b bg-white px-4 transition-all delay-100 print:hidden',
				'sticky inset-x-0 top-0 z-40',
				{
					'!fixed !border-b-0 !bg-transparent shadow-none [&_a]:!text-white [&_button]:!text-white':
						showTransparentBar,
				},
				className,
			)}
		>
			<BrandLink />
			{!showTransparentBar && (isAuth || pathname === '/') && (
				<FindACocoonBtn />
			)}
			<AppMenu />
		</nav>
	)
}
